// src/services/ReportService.js
//
// Attendance reports, served from Vero's own database rather than Microsoft
// Graph. These are the badge events recorded by the door webhook, joined to the
// names in UserTranslation, DepartmentTranslation and LocationTranslation.
//
// Note this is a different population from the site roster. The roster shows
// everyone in the directory; these reports show only people who have actually
// badged, which today means only those with a UserTranslation row.

import apiClient from './apiClient'

/**
 * One row per person per day: first in, last out, time inside, number of
 * visits, and counts of any unpaired events.
 */
export async function getDailyReport(filters) {
  const response = await apiClient.post('/GetPresenceDaily', filters)

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not load the report')
  }
  return response.data.rows ?? []
}

/**
 * Every badge event in the range, one row each, with no interpretation.
 */
export async function getDetailReport(filters) {
  const response = await apiClient.post('/GetPresenceDetail', filters)

  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message ?? 'Could not load the report')
  }
  return response.data.rows ?? []
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

/**
 * Quote a single CSV field.
 *
 * Excel and Numbers both need the RFC 4180 rules: wrap in quotes when the value
 * contains a comma, quote or newline, and double any embedded quotes. Skipping
 * this is how a department called "Sales, EU" silently becomes two columns.
 */
function csvField(value) {
  const s = value === null || value === undefined ? '' : String(value)
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

export function toCsv(columns, rows) {
  const header = columns.map((c) => csvField(c.label)).join(',')
  const body = rows.map((row) =>
    columns.map((c) => csvField(row[c.key])).join(',')
  )
  return [header, ...body].join('\r\n')
}

export function downloadCsv(filename, csv) {
  // The BOM is what makes Excel open a UTF-8 CSV correctly. Without it,
  // "Hillerød" arrives as "HillerÃ¸d" on a Windows machine.
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
